/**
 * AppPageColumn — the page region of the app shell (the scroller under the navbar and the
 * 1600px content column inside it). `__root.tsx` renders every routed page into it.
 *
 * `html`, `body` and `#root` are `height: 100%` (index.css), so this column has a DEFINITE
 * height, and `h-full` / `flex-1` resolve against it. Two kinds of page rely on that: the fill
 * pages (a `flex flex-col h-full` Tabs shell with a pinned header and a scrolling body, a
 * `flex-1 min-h-0` row). They keep working: nothing here changes the column's height.
 *
 * What it does change is what a bare `Card` does. ADR-024 makes `Card` fill its container
 * (`h-full`); a Card returned directly as page content is a flex item of this column, so it
 * resolved `h-full` to the whole page (measured > 500px in a 900px shell). The page decides: a
 * `Card` that is a DIRECT child of the column is content-height. A page that wants its Card to
 * fill opts in with `flex-1` (flex-grow, which the column honours) or wraps it in a fill
 * container; a Card inside any wrapper (`Page`, `PageContainer`, a `space-y` root, a grid) is
 * unaffected and still fills its own container.
 *
 * Why a selector on the column and not `h-auto` at each call site: a dozen pages return a Card
 * as their body (not-found, error and empty states, upload and invoice pages), and the next page
 * would have repeated the mistake. The rule lives where the definite height is introduced.
 */
import type { ReactNode } from 'react'

export function AppPageColumn({ children }: { children: ReactNode }) {
  return (
    <div className="flex-1 min-h-0 overflow-x-auto flex flex-col px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-24">
      <div
        className="w-full max-w-[1600px] mx-auto flex-1 flex flex-col [&>[data-slot=card]]:h-auto"
        data-testid="app-page-column"
      >
        {children}
      </div>
    </div>
  )
}
